/**
 * planUtils.js - Shared utility functions for dive plan display.
 *
 * Used by PlanView and PdfExport to avoid code duplication.
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 * AGPL-3.0-or-later - see LICENSE
 */

import { Buhlmann } from '../core/Buhlmann.js'
import { DivePlanner } from '../core/DivePlanner.js'
import { Gas } from '../core/Gas.js'

/**
 * Dive environment (altitude / water type) of the plan being displayed.
 *
 * planUtils is imported by the exporters (PdfExport, HtmlExport) and by the
 * views, and none of them passes an environment to calculateMOD, so the global
 * store is the single shared source here. Outside a browser (unit tests) an
 * empty object is returned, which Gas reads as sea level / salt water: the
 * historical behaviour, unchanged.
 *
 * @returns {Object} { altitude, waterType, gasTemp } or {}
 */
export function planEnvironment() {
  if (typeof window === 'undefined' || !window.store) return {}
  return window.store.state?.environment || {}
}

/**
 * Screen label for a gas: first space becomes a hyphen, the name is cut at the
 * next one ("TX 17/37" -> "TX-17/37", "TX14/30 (normo 70m)" -> "TX14/30-(normo").
 *
 * Cutting at the very first space, as this did, is right for a name whose tail
 * is a comment ("EAN32 Bailout" -> "EAN32") and wrong for a mix written with a
 * space ("TX 17/37" -> "TX"), which is then no longer a gas anybody can name -
 * and beside a "TX14/48" the diver reads two lines with no way to tell them
 * apart. Keeping the first word together with what follows it names the mix;
 * cutting at the second space keeps the column narrow enough to read.
 *
 * Documents do not use this: the PDF and the HTML export print the whole name.
 *
 * @param {string} name - Full gas name
 * @returns {string} Short gas name
 */
export function shortGasName(name) {
  if (!name) return ''
  const firstSpace = name.indexOf(' ')
  if (firstSpace <= 0) return name
  const hyphenated = `${name.slice(0, firstSpace)}-${name.slice(firstSpace + 1)}`
  const nextSpace = hyphenated.indexOf(' ', firstSpace + 1)
  return nextSpace > 0 ? hyphenated.slice(0, nextSpace) : hyphenated
}

/**
 * Display labels for a list of gas names, shortened only where shortening
 * still names one cylinder and one only.
 *
 * shortGasName cuts at the first space, which turns 'TX 17/37' into 'TX'. Next
 * to a 'TX14/48' the diver reads two lines, 'TX' and 'TX14/48', with no way to
 * tell which one the ascent breathes - and a printed plan that lists four
 * bailout cylinders and a requirement table saying 'TX' is exactly how an
 * untouched 12 L went unnoticed while another cylinder was flagged short.
 *
 * A short form is kept only when no other name of the list produces it too,
 * and when it does not read as an abbreviation of another name (i.e. is not
 * one of their prefixes). Otherwise both names are printed whole.
 *
 * @param {string[]} names - Full gas names, in display order
 * @returns {string[]} One label per name, same order
 */
export function gasLabels(names) {
  const list = Array.isArray(names) ? names : []
  const short = list.map(n => shortGasName(n))
  return list.map((full, i) => {
    const candidate = short[i]
    if (!candidate) return full || ''
    const lower = candidate.toLowerCase()
    const ambiguous = list.some((other, j) => {
      if (j === i) return false
      if (short[j] === candidate) return true
      return String(other || '').toLowerCase().startsWith(lower)
    })
    return ambiguous ? full : candidate
  })
}

// English fallbacks for the requirement table, used when no translation
// function is provided. Never the i18n key itself: a table printed by a tool
// must read as a table, not as a list of identifiers.
const GAS_STATUS_FALLBACK = Object.freeze({
  plan_ok: 'OK',
  plan_insufficient: 'INSUFFICIENT',
  plan_gas_tight: 'TIGHT',
  plan_gas_unused: 'NOT BREATHED',
  plan_gas_unused_detail: '{liters}L carried, untouched by this plan',
  plan_need_more: 'Need {liters}L ({bars} bar)',
  plan_left: '{liters}L left ({bars} bar)',
  plan_need_more_liters: 'Need {liters}L',
  plan_left_liters: '{liters}L left',
  plan_gas_backup: 'Backup: {names}'
})

/**
 * Rows of the bailout gas requirement table, the single source PlanView,
 * PdfExport and HtmlExport share.
 *
 * Every cylinder is listed, including the ones the plan never breathes. The
 * three surfaces used to filter on `liters > 0`, so a carried cylinder the
 * engine ignored simply disappeared from the sheet: page 1 announced it, the
 * requirement table did not mention it, and nothing said it stayed full while
 * another cylinder was short. A cylinder at 0 L is reported as unused - not as
 * OK, which would be a soundness claim about gas nobody opened.
 *
 * Pressures are only shown when a single cylinder carries the mix: adding bars
 * across a 7 L / 200 bar and a 12 L / 300 bar would print a pressure no gauge
 * ever shows.
 *
 * A cylinder the plan empties to the litre is reported as tight rather than OK
 * (DivePlanner.GAS_RESERVE_FRACTION), and the backup cylinders are named with
 * what the plan leaves in them, never with what they held at the surface.
 *
 * Labels are shortened for the screen and left whole for the documents: a table
 * cell can be reopened, a printed sheet cannot, so what is carried to the water
 * names its cylinders in full.
 *
 * @param {Object[]} consumption - result.gasConsumption entries
 * @param {Function} [t] - Translation function (key, params) => string
 * @param {Object} [options] - { shorten: true for the screen, false for documents }
 * @returns {Object[]} One row per cylinder line, in the given order
 */
export function gasConsumptionRows(consumption, t, options = {}) {
  const { shorten = true } = options
  const list = Array.isArray(consumption) ? consumption : []
  const translate = typeof t === 'function'
    ? t
    : (key, params) => {
      const raw = GAS_STATUS_FALLBACK[key] ?? key
      if (!params) return raw
      return Object.entries(params).reduce((s, [k, v]) => s.replace(`{${k}}`, v), raw)
    }
  // Labels are resolved over every name the table can print, its own lines and
  // the backup cylinders alike. Shortening the backup names on their own list
  // brought back the very ambiguity gasLabels exists to avoid: a single backup
  // named 'TX 17/37' collides with nothing inside a one name list and printed
  // as 'TX', right under a 'TX14/48' line.
  const names = [...new Set([
    ...list.map(c => c.name),
    ...list.flatMap(c => (Array.isArray(c.backup) ? c.backup.map(b => b.name) : []))
  ])]
  const labelOf = new Map((shorten ? gasLabels(names) : names).map((label, i) => [names[i], label]))
  const labels = list.map(c => labelOf.get(c.name) ?? c.name)

  return list.map((c, i) => {
    const tanks = Array.isArray(c.tanks) && c.tanks.length
      ? c.tanks
      : [{ volume: c.tankVolume, pressure: c.tankPressure }]
    const capacity = c.tankCapacity ?? tanks.reduce((sum, tk) => sum + (tk.volume || 0) * (tk.pressure || 0), 0)
    const singleTank = tanks.length === 1
    const totalVolume = tanks.reduce((sum, tk) => sum + (tk.volume || 0), 0)
    const unused = !(c.liters > 0)
    const insufficient = !unused && Boolean(c.insufficient)
    // Enough gas, but nothing left over. Said apart from a plain OK because the
    // two read the same in green while they are not the same plan.
    const tight = !unused && !insufficient && Boolean(c.tight)

    let statusText
    let detailText
    if (unused) {
      statusText = translate('plan_gas_unused')
      detailText = translate('plan_gas_unused_detail', { liters: Math.round(capacity) })
    } else if (insufficient) {
      statusText = translate('plan_insufficient')
      const missingLiters = Math.ceil(c.liters - capacity)
      detailText = singleTank
        ? translate('plan_need_more', { liters: missingLiters, bars: Math.ceil(missingLiters / (totalVolume || 1)) })
        : translate('plan_need_more_liters', { liters: missingLiters })
    } else {
      statusText = translate(tight ? 'plan_gas_tight' : 'plan_ok')
      const remainingLiters = Math.floor(capacity - c.liters)
      detailText = singleTank
        ? translate('plan_left', { liters: remainingLiters, bars: Math.floor(remainingLiters / (totalVolume || 1)) })
        : translate('plan_left_liters', { liters: remainingLiters })
    }

    // Backup cylinders carry what the plan leaves in them, not what they held
    // at the surface: 'Backup: TX 17/37' beside a shortfall meant nothing while
    // the same ascent had emptied that very cylinder.
    const backupText = Array.isArray(c.backup) && c.backup.length
      ? translate('plan_gas_backup', {
        names: c.backup
          .map(b => {
            const label = labelOf.get(b.name) ?? b.name
            return Number.isFinite(b.free) ? `${label} (${b.free}L)` : label
          })
          .join(', ')
      })
      : ''

    return {
      name: c.name,
      label: labels[i],
      tankText: tanks.map(tk => `${tk.volume}L @ ${tk.pressure}bar`).join(' + '),
      neededText: `${c.liters}L${singleTank ? ` (${c.barsNeeded} bar)` : ''}`,
      liters: c.liters,
      tankCapacity: capacity,
      insufficient,
      tight,
      unused,
      statusText,
      detailText,
      backupText
    }
  })
}

/**
 * Filter plan segments for display: show descent, bottom, stops, gas/diluent
 * changes, and only the first ascent (bottom -> first stop). Skip ascent
 * segments between deco stops (they are just 3m increments, not useful).
 *
 * @param {Object[]} segments - All plan segments
 * @returns {Object[]} Filtered segments for display
 */
export function filterDisplaySegments(segments) {
  if (!segments) return []
  let firstStopReached = false
  return segments.filter(s => {
    // 'flush' is kept whatever the phase: it happens on the way up, after the
    // first stop more often than not, and it is the one row that says the loop
    // left the setpoint.
    if (s.type === 'descent' || s.type === 'bottom' || s.type === 'gasChange'
        || s.type === 'diluentChange' || s.type === 'flush') {
      return true
    }
    if (s.type === 'stop') {
      firstStopReached = true
      return true
    }
    if (s.type === 'ascent' && !firstStopReached) {
      return true
    }
    return false
  })
}

/**
 * Get maximum depth from a plan's segments.
 * @param {Object} plan - Dive plan with segments array
 * @returns {number} Maximum depth in meters
 */
export function getMaxDepth(plan) {
  if (!plan || !plan.segments) return 0
  return Math.max(...plan.segments.map(s => s.depth || 0))
}

/**
 * Get total bottom time from a plan (sum of all 'bottom' segments).
 * @param {Object} plan - Dive plan with segments array
 * @returns {number} Bottom time in minutes (rounded)
 */
export function getBottomTime(plan) {
  if (!plan || !plan.segments) return 0
  return Math.round(plan.segments
    .filter(s => s.type === 'bottom')
    .reduce((sum, s) => sum + (s.time || 0), 0))
}

/**
 * Calculate Maximum Operating Depth for a gas at a given ppO2 limit.
 *
 * Delegates to Gas.calcMOD so the displayed MOD follows the same depth <->
 * pressure scale as the planner (altitude and water type included) instead of
 * the hard coded sea water / sea level conversion this helper used to inline.
 * A gas the core refuses (no or aberrant fO2) is logged and reported as 0 m
 * rather than aborting a PDF or HTML export.
 *
 * @param {Object} gas - Gas object with fO2 property
 * @param {number} ppO2Max - Maximum ppO2 (default 1.4)
 * @param {Object} [env] - Dive environment, defaults to the store environment
 * @returns {number} MOD in meters (floored, never negative)
 */
export function calculateMOD(gas, ppO2Max, env = planEnvironment()) {
  // `??` and not `||`: the default must cover an absent value, never a 0 that
  // would then be displayed as a usable MOD computed on another gas.
  const fO2 = gas?.fO2 ?? 0.21
  const ppO2 = gas?.ppO2Max ?? ppO2Max ?? 1.4
  try {
    return Gas.calcMOD(fO2, ppO2, env)
  } catch (e) {
    console.error('calculateMOD: cannot compute MOD for gas %o (fO2=%o, ppO2=%o): %s',
      gas?.name, fO2, ppO2, e.message)
    return 0
  }
}

/**
 * Convert plan segments into a flat array of {time, depth, ceiling} points
 * for chart rendering (profile view and PDF profile).
 * @param {Object} plan - Dive plan with segments array
 * @returns {Object[]|null} Array of profile points, or null if no segments
 */
export function getProfileData(plan) {
  if (!plan || !plan.segments || plan.segments.length === 0) return null

  const points = []

  // Start at surface
  points.push({ time: 0, depth: 0, ceiling: 0 })

  for (const seg of plan.segments) {
    if (seg.type === 'descent' || seg.type === 'ascent') {
      points.push({
        time: seg.runtime,
        depth: seg.endDepth,
        ceiling: Math.max(0, seg.ceiling || 0)
      })
    } else if (seg.type === 'bottom' || seg.type === 'stop'
        || ((seg.type === 'flush' || seg.type === 'gasChange') && seg.time > 0)) {
      // A flush and a timed gas change are minutes held at one depth, exactly
      // like a stop. Without their two points the ascent that follows them
      // started at the end of the previous row and absorbed their minutes:
      // a 3 min flush at 21 m drew a 21 -> 18 m leg four times slower than it
      // is. A zero length gas change is a marker and draws nothing.
      const startTime = seg.runtime - seg.time
      points.push({
        time: startTime,
        depth: seg.depth,
        ceiling: Math.max(0, seg.ceiling || 0)
      })
      points.push({
        time: seg.runtime,
        depth: seg.depth,
        ceiling: Math.max(0, seg.ceiling || 0)
      })
    }
  }

  // End at surface
  const lastPoint = points[points.length - 1]
  if (lastPoint.depth > 0) {
    points.push({ time: lastPoint.time + 1, depth: 0, ceiling: 0 })
  }

  return points
}

/**
 * Get translated label for the stress RMV phase setting.
 * @param {string} stressUntil - Setting value ('first_stop', 'gas_switch', 'surface')
 * @param {Function} t - Translation function
 * @returns {string} Translated label
 */
export function getStressUntilText(stressUntil, t) {
  switch (stressUntil) {
    case 'first_stop':
      return t('settings_stress_first_stop')
    case 'gas_switch':
      return t('settings_stress_gas_switch')
    case 'surface':
      return t('settings_stress_surface')
    default:
      return t('settings_stress_first_stop')
  }
}

/**
 * Format a duration in minutes for display ("15 h 20", "45 min").
 * The unit separators live in the language files, so each language keeps its
 * own typography.
 *
 * @param {number} minutes - Duration in minutes
 * @param {Function} t - Translation function
 * @returns {string} Localized duration, empty string on a non finite value
 */
export function formatDuration(minutes, t) {
  if (!Number.isFinite(minutes) || minutes < 0) {
    console.error('formatDuration: invalid duration (%o), nothing displayed', minutes)
    return ''
  }

  const total = Math.round(minutes)
  const hours = Math.floor(total / 60)
  const mins = total % 60

  if (hours === 0) return t('duration_minutes', { m: mins })
  if (mins === 0) return t('duration_hours', { h: hours })
  return t('duration_hours_minutes', { h: hours, m: String(mins).padStart(2, '0') })
}

/**
 * Sentence naming the DAN case a plan falls under, per noFlyReason. DECO and
 * HELIUM are the two halves of the same third case and get one sentence each:
 * both report a floor, and the diver has to know which one applies to the dive
 * he is holding.
 */
const DAN_CASE_KEYS = Object.freeze({
  [DivePlanner.NO_FLY_REASONS.NO_STOP]: 'plan_no_fly_dan_case_no_stop',
  [DivePlanner.NO_FLY_REASONS.REPETITIVE]: 'plan_no_fly_dan_case_repetitive',
  [DivePlanner.NO_FLY_REASONS.DECO]: 'plan_no_fly_dan_case_deco',
  [DivePlanner.NO_FLY_REASONS.HELIUM]: 'plan_no_fly_dan_case_helium'
})

/**
 * Sentences describing the surface interval that follows the dive: the
 * computed desaturation, then the DAN recommendation the no-fly cell comes
 * from - the case this plan falls under, the three published cases quoted in
 * full, and the source they are quoted from.
 *
 * The three cases are printed whole and not just the one that applies, because
 * the cell shows a single number and the diver has to be able to check it
 * against the recommendation itself. The source goes with them: 18 h surprises
 * enough people that the sentence has to say where it comes from and under
 * which conditions it was established.
 *
 * plan.desatTime is null when the computation did not settle within
 * Buhlmann.MAX_SURFACE_INTERVAL (48 h); the sentence then says "more than
 * 48 h" instead of dropping the information silently.
 *
 * @param {Object} plan - Computed dive plan
 * @param {Function} t - Translation function
 * @param {Object} [env] - Dive environment, defaults to the plan environment
 * @returns {Object} { desat, danCase, danRule, danSource }, each an empty
 *                   string when unavailable
 */
export function surfaceIntervalSentences(plan, t, env = planEnvironment()) {
  const empty = { desat: '', danCase: '', danRule: '', danSource: '' }
  if (!plan) return empty

  const altitude = Math.round(env.altitude || 0)

  let desat = ''
  if (plan.desatTime === null) {
    desat = t('plan_desat_over', { time: formatDuration(Buhlmann.MAX_SURFACE_INTERVAL, t) })
  } else if (Number.isFinite(plan.desatTime)) {
    const time = formatDuration(plan.desatTime, t)
    desat = altitude > 0
      ? t('plan_desat_altitude', { time, altitude })
      : t('plan_desat_sea_level', { time })
  }

  // Nothing is said at all when the plan carries no delay: there is no case to
  // name and no recommendation to quote against a missing number.
  if (!Number.isFinite(plan.noFlyTime)) return { ...empty, desat }

  const danRule = t('plan_no_fly_dan_rule', {
    noStop: formatDuration(DivePlanner.NO_FLY_NO_STOP, t),
    multiDay: formatDuration(DivePlanner.NO_FLY_REPETITIVE, t),
    longer: formatDuration(DivePlanner.NO_FLY_LONGER, t)
  })

  // The case this very plan falls under, named so the diver does not have to
  // guess which of the three the cell is showing - a second no-stop dive of the
  // day reports 18 h, and nothing else in the plan explains it.
  //
  // A plan carrying no noFlyReason was saved by an earlier version: the three
  // cases and the source are still worth printing, only the case is unknown, so
  // it is left out rather than guessed from the delay.
  const danCase = DAN_CASE_KEYS[plan.noFlyReason]
    ? t(DAN_CASE_KEYS[plan.noFlyReason])
    : ''

  return { desat, danCase, danRule, danSource: t('plan_no_fly_dan_source') }
}

/**
 * Breathing gas density, in g/l, above which the work of breathing and the
 * carbon dioxide retention that comes with it are flagged.
 *
 * 6.2 g/l is the hard ceiling of Anthony & Mitchell: beyond it the evidence on
 * CO2 retention makes the mix unsuitable whatever the circuit. 5.2 g/l is their
 * recommended working target, and it is applied to rebreathers only, where the
 * loop adds its own resistance on top of the gas.
 *
 * Both numbers were literals inside alerts.js, invisible to the tables that
 * display the density. Here once, so the colour of a row and the alert under
 * the table cannot say two different things.
 */
export const GAS_DENSITY_CRITICAL = 6.2
export const GAS_DENSITY_WARNING = 5.2

/**
 * Depth a segment is judged at: the depth it ends at.
 *
 * Transitions carry startDepth/endDepth and no depth, every other segment type
 * carries a single depth. Written once because the alert analysis and the
 * density column have to pick the same metre, otherwise a row can be coloured
 * green under an alert that fired on it.
 *
 * @param {Object} segment - Plan segment
 * @returns {number} Depth in meters
 */
export function segmentDepth(segment) {
  if (!segment) return 0
  return segment.depth || segment.endDepth || 0
}

/**
 * Breathing mix of a segment, or null when the segment carries none.
 *
 * Marker segments describe an event and no gas: the diluentChange pushed by
 * ccrPass has a name and a depth and nothing else. Returning null for those is
 * the point of this helper - reading them as air, which is what a `fO2 || 0.21`
 * default does, gave a 60 m diluent change the density of air at 60 m, 8.1 g/l,
 * and raised a critical density alert on a loop breathing trimix.
 *
 * @param {Object} segment - Plan segment
 * @returns {{fO2: number, fHe: number, fN2: number}|null} Mix, or null
 */
export function segmentMix(segment) {
  if (!segment || segment.fO2 === undefined || segment.fO2 === null) return null
  const fO2 = segment.fO2
  const fHe = segment.fHe || 0
  return { fO2, fHe, fN2: 1 - fO2 - fHe }
}

/**
 * Breathing gas density of a segment, in g/l, or null when it carries no mix.
 *
 * @param {Object} segment - Plan segment
 * @param {Object} environment - { altitude, waterType, gasTemp }
 * @returns {number|null} Density in g/l, or null
 */
export function segmentDensity(segment, environment = planEnvironment()) {
  const mix = segmentMix(segment)
  if (!mix) return null
  return Gas.gasDensity(mix.fO2, mix.fN2, mix.fHe, segmentDepth(segment), environment)
}

/**
 * Severity a density falls under, for both the alerts and the display.
 *
 * @param {number|null} density - Density in g/l
 * @param {boolean} isCCR - Whether the plan is dived on a rebreather
 * @returns {'critical'|'high'|'ok'|null} null when there is no density to judge
 */
export function densityLevel(density, isCCR = false) {
  if (!Number.isFinite(density)) return null
  if (density > GAS_DENSITY_CRITICAL) return 'critical'
  if (isCCR && density > GAS_DENSITY_WARNING) return 'high'
  return 'ok'
}

/**
 * Density of a segment as the tables print it: one decimal, or a dash.
 *
 * @param {Object} segment - Plan segment
 * @param {Object} environment - { altitude, waterType, gasTemp }
 * @returns {string} Formatted density, '-' when the segment carries no mix
 */
export function formatDensity(segment, environment = planEnvironment()) {
  const density = segmentDensity(segment, environment)
  return Number.isFinite(density) ? density.toFixed(1) : '-'
}

/**
 * What a mix is worth at a depth: the three figures a flush is judged on.
 *
 * A loop flush is the one moment of a rebreather dive where the setpoint does
 * not hold, so the depth alone says nothing: air at 35 m and air at 55 m are the
 * same row on screen and two very different minutes. ppO2 is the one the engine
 * really loads the tissues with (fO2 * pAmb, no setpoint), so the flush editor
 * and the computed plan cannot print two different numbers for the same metre.
 *
 * Density and not viscosity: the work of breathing a rebreather loop is driven
 * by gas density, which is what the plan rows, the alerts and their thresholds
 * are all expressed in here.
 *
 * @param {Object|null} mix - { fO2, fHe }, a diluent or any breathing mix
 * @param {number} depth - Depth in metres
 * @param {Object} environment - { altitude, waterType, gasTemp }
 * @returns {{ppO2: number, end: number, density: number}|null} null when the
 *   mix or the depth is not usable, which is what an editor being typed into
 *   looks like
 */
export function flushFigures(mix, depth, environment = planEnvironment()) {
  if (!mix || !Number.isFinite(mix.fO2) || mix.fO2 <= 0 || mix.fO2 > 1) return null
  if (!Number.isFinite(depth) || depth < 0) return null
  const fHe = mix.fHe || 0
  return {
    ppO2: Gas.ppO2(mix.fO2, depth, environment),
    end: Gas.calcEND(depth, mix.fO2, fHe, environment),
    density: Gas.gasDensity(mix.fO2, 1 - mix.fO2 - fHe, fHe, depth, environment)
  }
}

/**
 * Depth a flush target lands on, as the flush editor writes it.
 *
 * Nobody plans a flush by saying "at 46.5 m": he says "on air, where it gives
 * me 1.2 bar". This is that sentence, and the depth is the only thing the plan
 * keeps - storing the target instead would move the flush the day the mix, the
 * water or the altitude changes, and a saved plan would replay somewhere else
 * than where its author wrote it.
 *
 * Rounded down, to the shallower metre: the answer is a real number, the field
 * takes whole metres, and one metre shallower lowers the ppO2 rather than
 * raising it. `figures` is therefore computed at the metre retained and never
 * at the exact depth, so the screen cannot promise 1.30 bar for a plan that
 * will breathe 1.32.
 *
 * Here rather than in the view because it is the rule, not the rendering: the
 * tests hold it without a DOM, and the view cannot drift from what is tested.
 *
 * @param {Object|null} mix - Flush mix, from the diluent list
 * @param {'ppO2'|'end'} kind - What the diver aims at
 * @param {number} target - Value aimed at, bar or metres
 * @param {Object} environment - { altitude, waterType, gasTemp }
 * @returns {{depth: number|null, exact: number|null, figures: Object|null, reason: string|null}}
 *   depth is null when no depth answers, and reason then says why:
 *   'no_nitrogen' for an END asked of a mix carrying none, 'unreachable' when
 *   the depth would be at or above the surface, null when there is simply
 *   nothing to compute yet
 */
export function flushDepthForTarget(mix, kind, target, environment = planEnvironment()) {
  if (!mix || !Number.isFinite(target) || target <= 0) {
    return { depth: null, exact: null, figures: null, reason: null }
  }

  // A diluent carrying an impossible fraction makes Gas throw, and a throw
  // while the diver is typing would take the editor down with it.
  let exact
  try {
    exact = kind === 'end'
      ? Gas.depthForEND(target, mix.fO2, mix.fHe || 0, environment)
      : Gas.depthForPpO2(mix.fO2, target, environment)
    if (exact === null) return { depth: null, exact: null, figures: null, reason: 'no_nitrogen' }
  } catch (error) {
    console.error('flushDepthForTarget: no depth for %s = %o on %o: %s', kind, target, mix?.name, error.message)
    return { depth: null, exact: null, figures: null, reason: 'unreachable' }
  }

  const depth = Math.floor(exact)
  if (!Number.isFinite(depth) || depth < 1) {
    return { depth: null, exact, figures: null, reason: 'unreachable' }
  }
  return { depth, exact, figures: flushFigures(mix, depth, environment), reason: null }
}

/**
 * CNS percentage above which the oxygen exposure is flagged on the screens and
 * in the exports. The NOAA single exposure limit is 100 %; 80 % is the usual
 * warning threshold, and it was already the value hard coded in the plan
 * screen, the PDF and the HTML export - here once, so the three cannot drift.
 */
export const CNS_WARNING_PERCENT = 80

/**
 * OTU above which the daily pulmonary exposure is flagged. 300 OTU per day is
 * the accepted working limit, and what the whole application already used.
 */
export const OTU_DAILY_LIMIT = 300

/**
 * Oxygen exposure to display for a plan, and whether it has to be flagged.
 *
 * The number shown is the one of the SERIES when the plan carries it: a second
 * dive that gained 40 % of CNS while the day sits at 100 % must not print 40
 * under a label the diver reads as his oxygen clock. cnsTotal and otuTotal
 * equal cns and otu on a first dive of the day, so nothing changes for a single
 * dive; a plan saved before the series existed carries neither, and the dive
 * alone is then all there is to show.
 *
 * @param {Object} plan - Computed dive plan
 * @returns {Object} { cns, otu, cnsWarning, otuWarning, series }
 */
export function exposureShown(plan) {
  if (!plan) {
    console.error('exposureShown: no plan given, nothing to display')
    return { cns: 0, otu: 0, cnsWarning: false, otuWarning: false, series: false }
  }

  const cns = Number.isFinite(plan.cnsTotal) ? plan.cnsTotal : (Number.isFinite(plan.cns) ? plan.cns : 0)
  const otu = Number.isFinite(plan.otuTotal) ? plan.otuTotal : (Number.isFinite(plan.otu) ? plan.otu : 0)

  return {
    cns,
    otu,
    cnsWarning: cns > CNS_WARNING_PERCENT,
    otuWarning: otu > OTU_DAILY_LIMIT,
    // True when the figures cover more than this dive, so the screens can say
    // so instead of letting the diver read a day total as a dive total
    series: plan.repetitive === true
  }
}

/**
 * Value shown in the no-fly summary cell: the DAN recommended surface
 * interval before flying, already formatted.
 *
 * The third DAN case - compulsory decompression, or a helium mix - has no
 * published figure, only "substantially longer than 18 hours". The cell then
 * prints "> 18 h" and not "18 h": the plan carries noFlyMinimum for exactly
 * that, and a firm number would be a figure DAN never gave. A plan saved by an
 * earlier version carries no noFlyMinimum and keeps the firm rendering it was
 * saved with.
 *
 * @param {Object} plan - Computed dive plan
 * @param {Function} t - Translation function
 * @returns {string} Localized duration, empty string when unavailable
 */
export function noFlyValue(plan, t) {
  if (!plan || !Number.isFinite(plan.noFlyTime)) return ''
  const time = formatDuration(plan.noFlyTime, t)
  return plan.noFlyMinimum === true ? t('plan_no_fly_minimum', { time }) : time
}

// English labels used when no translation function is provided, so the
// fallback stays a readable word and never leaks a raw i18n key.
const SEGMENT_TYPE_FALLBACK = Object.freeze({
  segment_bail: 'BAIL',
  segment_desc: 'Desc',
  segment_asc: 'Asc',
  segment_bottom: 'Bottom',
  segment_stop: 'Stop',
  segment_gas_change: 'Gas sw.',
  segment_diluent_change: 'Dil. sw.',
  segment_flush: 'Flush',
  segment_safety_stop: 'Safety'
})

/**
 * Get segment type label for PDF/HTML display.
 *
 * The labels are short on purpose: the Type column of the plan table is 22 pt
 * wide in the PDF, about ten characters at font size 8.
 *
 * Localized like the rest of the table, whose headers already are. These
 * labels used to be hard coded, and one of them ('Fond') was French, so an
 * English PDF printed a French word in the middle of an English column; the
 * default branch printed the raw internal type ('gasChange'). The translation
 * function is optional so a caller without one - a test, a tool - still gets
 * the English abbreviation instead of nothing.
 *
 * @param {Object} segment - Plan segment
 * @param {Function} [t] - Translation function (key) => string
 * @returns {string} Short type label
 */
export function getSegmentTypeLabel(segment, t) {
  const translate = typeof t === 'function' ? t : (key) => SEGMENT_TYPE_FALLBACK[key] ?? key
  if (segment.isBailDelay) return translate('segment_bail')
  // A safety stop is a stop row flagged by the engine: the table has to say
  // that it is held by choice, not owed to the tissues
  if (segment.type === 'stop' && segment.safety) return translate('segment_safety_stop')
  switch (segment.type) {
    case 'descent': return translate('segment_desc')
    case 'ascent': return translate('segment_asc')
    case 'bottom': return translate('segment_bottom')
    case 'stop': return translate('segment_stop')
    case 'bail': return translate('segment_bail')
    case 'gasChange': return translate('segment_gas_change')
    case 'diluentChange': return translate('segment_diluent_change')
    case 'flush': return translate('segment_flush')
    default: return segment.type || '-'
  }
}
